import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Typography,
    Table,
    TableBody,
    MenuItem,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    CircularProgress,
    IconButton,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Edit as EditIcon,
    CheckCircle as CompleteIcon,
    PhotoCamera as PhotoIcon,
    PlayArrow as StartGradingIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import apiClient from '../../../api/client';

interface ReceivingItem {
    id: number;
    item_id: number;
    item_name: string;
    ordered_quantity: number;
    gross_received: number;
    damage: number;
    reject: number;
    final_accepted: number;
    damage_cost_allocation?: string;
    reject_cost_allocation?: string;
}

interface ReceivingDetails {
    id: number;
    receiving_number: string;
    po_number: string;
    batch_number: string;
    vendor_name: string;
    receiving_date: string;
    receiver_id?: string;
    receiver_name?: string;
    status: string;
    items: ReceivingItem[];
}

const ReceivingDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [receiving, setReceiving] = useState<ReceivingDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedItems, setEditedItems] = useState<Record<number, ReceivingItem>>({});
    const [users, setUsers] = useState<Array<{ id: string, full_name: string }>>([]);
    const [selectedReceiverId, setSelectedReceiverId] = useState('');

    useEffect(() => {
        fetchDetails();
        fetchUsers();
    }, [id]);

    const fetchDetails = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/receiving/${id}`);
            setReceiving(response.data);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to fetch receiving details');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await apiClient.get('/admin/users', {
                params: { is_active: true, limit: 100 }
            });
            if (response.data.users) {
                setUsers(response.data.users.map((u: any) => ({
                    id: u.id,
                    full_name: u.full_name || u.email
                })));
            }
        } catch (err) {
            console.error('Failed to fetch users:', err);
        }
    };

    const handleSave = async () => {
        try {
            const itemsArray = Object.values(editedItems);
            const updateData: any = { items: itemsArray };
            if (selectedReceiverId) {
                updateData.receiver_id = selectedReceiverId;
            }
            await apiClient.put(`/receiving/${id}`, updateData);
            setIsEditing(false);
            setEditedItems({});
            setSelectedReceiverId('');
            fetchDetails();
        } catch (err: any) {
            alert(err.response?.data?.detail || 'Failed to save changes');
        }
    };

    const handleComplete = async () => {
        try {
            await apiClient.post(`/receiving/${id}/complete`);
            setCompleteDialogOpen(false);
            fetchDetails();
        } catch (err: any) {
            alert(err.response?.data?.detail || 'Failed to complete receiving');
        }
    };

    const handleStartGrading = async () => {
        try {
            const response = await apiClient.post('/grading/create-from-receiving', null, {
                params: { receiving_id: id }
            });
            navigate(`/inward/grading/${response.data.id}`);
        } catch (err: any) {
            alert(err.response?.data?.detail || 'Failed to start grading');
        }
    };

    const handleItemChange = (itemId: number, field: keyof ReceivingItem, value: number) => {
        setEditedItems(prev => ({
            ...prev,
            [itemId]: {
                ...(prev[itemId] || receiving!.items.find(i => i.item_id === itemId)!),
                [field]: value
            }
        }));
    };

    const getItemValue = (item: ReceivingItem, field: keyof ReceivingItem) => {
        return editedItems[item.item_id]?.[field] ?? item[field];
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !receiving) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error || 'Receiving not found'}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/inward/receiving')}>
                        <BackIcon />
                    </IconButton>
                    <Typography variant="h4">Receiving - {receiving.receiving_number}</Typography>
                    <Chip label={receiving.status.toUpperCase()} color="primary" />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {receiving.status === 'draft' && (
                        <>
                            {isEditing ? (
                                <>
                                    <Button
                                        variant="outlined"
                                        onClick={() => { setIsEditing(false); setEditedItems({}); }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleSave}
                                    >
                                        Save
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => setIsEditing(true)}
                                >
                                    Edit
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<CompleteIcon />}
                                onClick={() => setCompleteDialogOpen(true)}
                            >
                                Complete
                            </Button>
                        </>
                    )}
                    {(receiving.status === 'completed' || receiving.status === 'grading_started') && (
                        <Button
                            variant="contained"
                            startIcon={<StartGradingIcon />}
                            onClick={handleStartGrading}
                        >
                            Start Grading
                        </Button>
                    )}
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Details</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">PO Number</Typography>
                                    <Typography variant="body1">{receiving.po_number}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Batch Number</Typography>
                                    <Typography variant="body1">{receiving.batch_number}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Vendor</Typography>
                                    <Typography variant="body1">{receiving.vendor_name}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Receiving Date</Typography>
                                    <Typography variant="body1">
                                        {format(new Date(receiving.receiving_date), 'dd MMM yyyy')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Receiver</Typography>
                                    {isEditing ? (
                                        <TextField
                                            select
                                            size="small"
                                            value={selectedReceiverId || receiving.receiver_id || ''}
                                            onChange={(e) => setSelectedReceiverId(e.target.value)}
                                            fullWidth
                                        >
                                            <MenuItem value="">Select Receiver</MenuItem>
                                            {users.map(user => (
                                                <MenuItem key={user.id} value={user.id}>{user.full_name}</MenuItem>
                                            ))}
                                        </TextField>
                                    ) : (
                                        <Typography variant="body1">{receiving.receiver_name || '-'}</Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Items</Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Ordered Qty</TableCell>
                                    <TableCell align="right">Gross Received</TableCell>
                                    <TableCell align="right">Damage</TableCell>
                                    <TableCell align="right">Reject</TableCell>
                                    <TableCell align="right">Final Accepted</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {receiving.items.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{item.item_name}</TableCell>
                                        <TableCell align="right">
                                            <Typography color="text.secondary">{item.ordered_quantity}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {isEditing ? (
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={getItemValue(item, 'gross_received')}
                                                    onChange={(e) => handleItemChange(item.item_id, 'gross_received', Number(e.target.value))}
                                                    sx={{ width: 100 }}
                                                />
                                            ) : (
                                                item.gross_received
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isEditing ? (
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={getItemValue(item, 'damage')}
                                                    onChange={(e) => handleItemChange(item.item_id, 'damage', Number(e.target.value))}
                                                    sx={{ width: 100 }}
                                                />
                                            ) : (
                                                item.damage
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isEditing ? (
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={getItemValue(item, 'reject')}
                                                    onChange={(e) => handleItemChange(item.item_id, 'reject', Number(e.target.value))}
                                                    sx={{ width: 100 }}
                                                />
                                            ) : (
                                                item.reject
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="bold">{item.final_accepted}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Complete Dialog */}
            <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)}>
                <DialogTitle>Complete Receiving?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This will mark the receiving as complete and log all wastage events.
                        Make sure all photos are uploaded.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCompleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleComplete} variant="contained">
                        Complete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReceivingDetailPage;
